/**
 * Applica le modifiche dell'editor a documenti che NON sono aperti.
 *
 * L'editor tiene in memoria il PDF del solo documento a schermo; per gli
 * altri, la revisione (i riquadri) sta nello stato della pagina. Applicarla
 * vuol dire rifare per ciascuno quello che l'editor fa per il proprio: prendere
 * l'originale dal backend, aprirlo con pdf.js per conoscere le pagine,
 * tradurre i riquadri nel formato del motore e chiedere la redazione.
 *
 * Le dipendenze sono iniettabili: pdf.js non gira sotto jsdom, e le prove
 * devono poter verificare la sequenza senza aprire un PDF vero.
 */
import { fetchEditorPayload as fetchEditorPayloadVero, redactPdf as redactPdfVero } from "./editorApi";
import { buildRedactPayload as buildRedactPayloadVero, groupSospetti } from "./bboxUtils";
import { loadPdfJs as loadPdfJsVero, loadPdfDocumentFromBytes as loadPdfDocumentFromBytesVero } from "../pdfjsLoader";

/** Il suffisso non si accumula: "x_anonimizzato_anonimizzato.pdf" non deve esistere. */
export function nomeAnonimizzato(nome) {
  const base = (nome || "document")
    .replace(/\.pdf$/i, "")
    .replace(/_(anonimizzato|redacted)$/i, "");
  return `${base}_anonimizzato.pdf`;
}

/**
 * @param {{jobId: string, fileIndex: number, sessionId?: string|null, bboxes: object[], confermati?: string[]}} voce
 *   confermati: le segnalazioni gia' guardate nell'editor (id dei gruppi),
 *   che partono con `controllato` come farebbe l'editor stesso
 * @returns {Promise<{bytes: ArrayBuffer, filename: string}>}
 */
export async function applicaRedazione({ jobId, fileIndex, sessionId = null, bboxes, confermati = [] }, deps = {}) {
  const fetchEditorPayload = deps.fetchEditorPayload || fetchEditorPayloadVero;
  const redactPdf = deps.redactPdf || redactPdfVero;
  const buildRedactPayload = deps.buildRedactPayload || buildRedactPayloadVero;
  const loadPdfJs = deps.loadPdfJs || loadPdfJsVero;
  const loadPdfDocumentFromBytes = deps.loadPdfDocumentFromBytes || loadPdfDocumentFromBytesVero;

  if (!Array.isArray(bboxes) || !bboxes.length) throw new Error("Nessuna area da applicare.");
  const payload = await fetchEditorPayload(jobId, fileIndex, sessionId);
  const pdfjs = await loadPdfJs();
  const doc = await loadPdfDocumentFromBytes(pdfjs, payload.pdfBytes.slice(0));
  try {
    const gruppi = new Set(Array.isArray(confermati) ? confermati : []);
    const controllati = new Set(
      groupSospetti(bboxes).filter((r) => gruppi.has(r.id)).flatMap((r) => r.items.map((w) => w.id)),
    );
    const apiBboxes = await buildRedactPayload(doc, bboxes, controllati);
    const bytes = await redactPdf(jobId, fileIndex, apiBboxes, sessionId);
    return { bytes, filename: nomeAnonimizzato(payload.pdfFilename || payload.originalFilename) };
  } finally {
    try { await doc.destroy?.(); } catch (_) { /* il documento e' gia' chiuso */ }
  }
}

/**
 * Applica in sequenza, uno alla volta: ogni redazione impegna il motore per
 * qualche secondo e le richieste parallele si farebbero solo la coda.
 * Un errore su un documento non ferma gli altri: viene riportato a parte.
 *
 * @param {Array<{indice: number, nome?: string, jobId: string, fileIndex: number, sessionId?: string|null, bboxes: object[]}>} voci
 * @param {{onAvanzamento?: (s: {indice: number|null, fatti: number, totale: number}) => void}} [opzioni]
 */
export async function applicaRedazioni(voci, { onAvanzamento, ...deps } = {}) {
  const fatti = [];
  const errori = [];
  const totale = voci.length;
  for (let n = 0; n < totale; n += 1) {
    const voce = voci[n];
    onAvanzamento?.({ indice: voce.indice, fatti: n, totale });
    try {
      const esito = await applicaRedazione(voce, deps);
      fatti.push({ indice: voce.indice, ...esito });
    } catch (e) {
      errori.push({ indice: voce.indice, nome: voce.nome || `documento ${voce.indice + 1}`, messaggio: e instanceof Error ? e.message : String(e) });
    }
  }
  onAvanzamento?.({ indice: null, fatti: totale, totale });
  return { fatti, errori };
}
